import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, FormHelperText, InputAdornment, MenuItem } from "@mui/material"
import { FormikDropdown, FormikSwitch, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { SelectHardwarePackages } from "./formElements/selectHardwarePackages/SelectHardwarePackages"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { MAX_NAME_LENGTH } from "../../../../../constants/inputValidationDefaults"
import { SelectPaymentDetails } from "./formElements/SelectPaymentDetails"
import { CreateOrEditShopFormValues, ShopTypes } from "../shopsTypes"
import { PublicFormContent } from "./formElements/PublicFormContent"
import { iterateStringEnum } from "../../../../../lib/enums"

const useStyles = makeStyles()(() => ({
    dropdown: {
        maxWidth: "27rem",
    },
    helperText: {
        marginLeft: "0.875rem",
        marginRight: "0.875rem",
    },
}))

export const FormShopContentGeneral = () => {
    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<CreateOrEditShopFormValues>()

    const handleChangeIsPublicShop = (checked: boolean) => {
        setFieldValue("shopDetails.isPublic", checked)
        checked
            ? setFieldValue("shopDetails.publicShopDetails.isVisibleForAppUsers", checked)
            : setFieldValue("shopDetails.publicShopDetails", undefined)
    }
    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="merchant-shop-form-field-name" attrs={{ label: true, helperText: true }}>
                    <FormikTextField<CreateOrEditShopFormValues>
                        name="name"
                        label="Name"
                        helperText="The shop name will be visible for your customers."
                        fullWidth
                        inputProps={{ maxLength: MAX_NAME_LENGTH }}
                    />
                </LocalizedStrict>
                <Box>
                    <LocalizedStrict id="merchant-shop-form-field-type" attrs={{ label: true }}>
                        <FormikDropdown<CreateOrEditShopFormValues>
                            name="type"
                            fullWidth
                            className={classes.dropdown}
                            label="Language"
                        >
                            {[...iterateStringEnum(ShopTypes)].map((type) => (
                                <MenuItem key={type} value={type}>
                                    <LocalizedEnum base="merchant-shop-form-field-type-mode" enumValue={type}>
                                        <span>{type}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-shop-form-field-type-helperText">
                        <FormHelperText className={classes.helperText}>Select the type of shop</FormHelperText>
                    </LocalizedStrict>
                </Box>
            </StandardMainPanel>
            {values.type && (
                <>
                    <StandardMainPanel>
                        <SelectHardwarePackages />
                    </StandardMainPanel>
                    <StandardMainPanel>
                        <SelectPaymentDetails />
                    </StandardMainPanel>
                </>
            )}
            <LocalizedStrict id="merchant-shop-form-make-shop-public" attrs={{ title: true, offContent: true }}>
                <StandardMainPanel
                    variant="switchable"
                    title="Public shop"
                    checked={values.shopDetails.isPublic}
                    onCheckChange={handleChangeIsPublicShop}
                    offContent="This shop is only accessible to invited companies."
                >
                    <PublicFormContent />
                </StandardMainPanel>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-shop-form-general" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="General">
                    <LocalizedStrict id="merchant-shop-form-field-active">
                        <FormikSwitch<CreateOrEditShopFormValues> name="isActive">Active:</FormikSwitch>
                    </LocalizedStrict>
                    <LocalizedStrict id="merchant-shop-form-field-demo">
                        <FormikSwitch<CreateOrEditShopFormValues> name="demoMode">Demo:</FormikSwitch>
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-shop-form-erp" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="ERP">
                    <LocalizedStrict
                        id="merchant-shop-form-field-case-number"
                        attrs={{ label: true, helperText: true }}
                    >
                        <FormikTextField<CreateOrEditShopFormValues>
                            name="caseNumber"
                            label="Case number"
                            helperText="The case number is used to import sales data into your organizations ERP."
                            fullWidth
                        />
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-shop-form-internal-note" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Internal note">
                    <LocalizedStrict id="merchant-shop-form-field-internal-note" attrs={{ label: true }}>
                        <FormikTextField<CreateOrEditShopFormValues>
                            name="internalNote"
                            label="Internal note:"
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="start">
                                        <FontAwesomeIcon icon={faPen} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
        </StandardForm>
    )
}
